/**
 * Session cache for the BP leaderboards' `targetId → {label, url}` join.
 *
 * WHY A CACHE AND NOT JUST THE CALL
 * One lookup is one `POST …/targets/by-id`, but server-side it is one S3 GET per
 * DISTINCT HOME SHARD (`content_read.handle_get_targets_by_id`, 16-way parallel) —
 * and the leaderboards are scoped to one category, so 100 ids land in one partition
 * whose shard count is capped at `TARGET_LIMITS.maxShardsPerPartition` = 64. On a
 * 300k-product catalogue that is up to 64 gzipped shard reads of ~4.7k rows each.
 *
 * Bounded by SHARD COUNT rather than by catalogue size, so it does not blow up — but
 * it is far too much to repeat on every window change, tag filter, tab switch and
 * axis switch, which is what the first version did. The ids barely move between
 * those. So: resolve each id ONCE per browser session, ask only for the misses, and
 * dedupe concurrent asks for the same id.
 *
 * Same posture as `useDomainFavicon`: module scope, survives remounts, keyed so one
 * brand's answer can never be read as another's.
 *
 * ⚠ STALENESS IS ACCEPTED, DELIBERATELY. A label renamed in Cibles keeps its old
 * value until the tab is reloaded. It is a display name on a leaderboard, and the
 * alternative is 64 shard reads per interaction. `notFound` is cached too — an id the
 * catalogue does not hold will not start holding it mid-session. If this ever needs
 * to be live, invalidate on `targetsHash` rather than shortening the lifetime.
 */

import { TARGET_LIMITS, type TargetCategory } from '@traaker/shared';
import { targetsService } from '../../services/targets';

/**
 * One row's display fields. `notFound` is kept as a DISTINCT state from "not asked":
 * an id the catalogue no longer holds is a different thing from one nobody looked up,
 * and neither may become an invented name.
 */
export type TargetLabel = { label?: string; url?: string; notFound?: true };

export interface TargetLabelAsk {
  targetId: string;
  /**
   * Load-bearing, not a nicety: with it the reader computes ONE candidate shard for
   * the id; without it, one per owner category (six partitions today), so a missing
   * hint multiplies the S3 reads. `bp_facts` writes `ct` on a top entry whenever the
   * fold knew it.
   */
  category?: TargetCategory;
}

const cache = new Map<string, TargetLabel>();
const inflight = new Map<string, Promise<void>>();

const keyOf = (brandId: string, targetId: string) => `${brandId}:${targetId}`;

/** Test seam + a way to drop the cache when a catalogue write lands in this tab. */
export function clearTargetLabelCache(): void {
  cache.clear();
  inflight.clear();
}

export type TargetLabelFetcher = typeof targetsService.getByIds;

/**
 * Resolve `asked` to a map, hitting the network only for ids never seen in this
 * session. The returned map contains ONLY the ids asked for — never the whole cache,
 * so a caller cannot accidentally render a label belonging to a previous window.
 *
 * A failed request resolves to whatever is already known rather than throwing: the
 * cards degrade to their url → domain → id-code chain, which is exactly the
 * no-join behaviour, so nothing on screen waits on this.
 */
export async function resolveTargetLabels(
  brandId: string,
  asked: TargetLabelAsk[],
  fetcher: TargetLabelFetcher = targetsService.getByIds,
): Promise<Map<string, TargetLabel>> {
  const unique = new Map<string, TargetLabelAsk>();
  for (const a of asked) {
    if (a.targetId && !unique.has(a.targetId)) unique.set(a.targetId, a);
  }

  const misses = [...unique.values()].filter((a) => !cache.has(keyOf(brandId, a.targetId)));
  // One in-flight promise per id, so two cards (or a re-render) asking at once make
  // one request between them instead of two identical ones.
  const pending = misses.filter((a) => inflight.has(keyOf(brandId, a.targetId)));
  const toFetch = misses.filter((a) => !inflight.has(keyOf(brandId, a.targetId)));

  if (toFetch.length) {
    const batch = toFetch.slice(0, TARGET_LIMITS.maxTargetsByIdLookup);
    const run = fetcher(brandId, batch)
      .then((resp) => {
        for (const it of resp.items) {
          cache.set(keyOf(brandId, it.targetId), { label: it.label, url: it.url });
        }
        for (const id of resp.notFound) {
          cache.set(keyOf(brandId, id), { notFound: true });
        }
      })
      .catch(() => {
        // Nothing cached: a transient failure must not become a permanent « non
        // résolue » for the rest of the session.
      })
      .finally(() => {
        for (const a of batch) inflight.delete(keyOf(brandId, a.targetId));
      });
    for (const a of batch) inflight.set(keyOf(brandId, a.targetId), run);
  }

  await Promise.all(
    [...pending, ...toFetch]
      .map((a) => inflight.get(keyOf(brandId, a.targetId)))
      .filter((p): p is Promise<void> => !!p),
  );

  const out = new Map<string, TargetLabel>();
  for (const targetId of unique.keys()) {
    const hit = cache.get(keyOf(brandId, targetId));
    if (hit) out.set(targetId, hit);
  }
  return out;
}
